// src/pages/public/AuthAction.jsx
//
// Pantalla que captura los enlaces de Firebase Auth (verifyEmail /
// resetPassword) generados server-side via Cloud Function (sendVerificationEmailFn /
// sendPasswordResetEmailFn) y enviados por Resend con templates custom.
//
// El Action URL custom se configura en Firebase Console → Authentication
// → Templates apuntando a https://edgeelite.cl/auth/action. Firebase añade
// los query params: mode, oobCode, apiKey, continueUrl, lang.
//
// Modos soportados:
//   - mode=verifyEmail   → applyActionCode(auth, oobCode) → flash success → /login
//   - mode=resetPassword → verifyPasswordResetCode + form nueva contraseña
//                          + confirmPasswordReset → flash success → /login
//   - cualquier otro     → mensaje de error ("operación no soportada")
//
// El flash success se pasa a /login via location.state.flash. La pantalla de
// login decide si lo muestra (no es responsabilidad de esta pantalla).

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, firestore } from '../../firebase';
import { AuthLogo, Field, ErrorBanner } from './_authShared';

const REDIRECT_DELAY_MS = 1800;

function actionErrorMessage(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/expired-action-code':
      return 'El enlace expiró. Vuelve a solicitar el email.';
    case 'auth/invalid-action-code':
      return 'El enlace ya se usó o no es válido. Vuelve a solicitar el email.';
    case 'auth/user-disabled':
      return 'Esta cuenta está deshabilitada. Escribe a soporte@edgeelite.cl.';
    case 'auth/user-not-found':
      return 'No encontramos una cuenta asociada a este enlace.';
    case 'auth/weak-password':
      return 'La contraseña debe tener al menos 8 caracteres.';
    case 'auth/network-request-failed':
      return 'Sin conexión. Revisa tu internet e intenta de nuevo.';
    default:
      return code ? `Error: ${code}` : 'Ocurrió un error. Intenta de nuevo.';
  }
}

export default function AuthAction() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const mode = params.get('mode');
  const oobCode = params.get('oobCode');

  // Estado UI:
  //   verifying  → llamando a applyActionCode / verifyPasswordResetCode
  //   form       → resetPassword: mostrando form de nueva contraseña
  //   submitting → confirmPasswordReset en curso
  //   success    → mostrar tick + redirect
  //   error      → mensaje de error
  //   unsupported→ modo desconocido
  const [status, setStatus] = useState(mode === 'resetPassword' ? 'verifying' : 'verifying');
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState(''); // email asociado al oobCode
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Modo no soportado o sin oobCode ───
  useEffect(() => {
    if (!mode || !oobCode) {
      setStatus('unsupported');
      setError('Enlace incompleto. Vuelve a abrirlo desde tu correo.');
      return;
    }
    if (mode !== 'verifyEmail' && mode !== 'resetPassword') {
      setStatus('unsupported');
      setError('Operación no soportada.');
    }
  }, [mode, oobCode]);

  // ─── verifyEmail: aplicar el código y redirigir ───
  useEffect(() => {
    if (mode !== 'verifyEmail' || !oobCode) return;
    let cancelled = false;
    (async () => {
      try {
        await applyActionCode(auth, oobCode);
        if (cancelled) return;

        // Sync Auth → Firestore para disparar onUserVerifiedSendWelcome.
        // applyActionCode solo actualiza el record de Firebase Auth; la
        // Cloud Function escucha cambios en Firestore. Best-effort: si
        // falla (e.g. usuario clickeó link en navegador sin sesión activa),
        // AuthContext.jsx hace backfill al próximo login.
        try {
          if (auth.currentUser) {
            await auth.currentUser.reload();
            await updateDoc(doc(firestore, 'users', auth.currentUser.uid), {
              emailVerified: true,
              verifiedAt: serverTimestamp(),
            });
          }
        } catch (syncErr) {
          console.warn('[AuthAction] Sync emailVerified failed (non-fatal):', syncErr);
        }

        setStatus('success');
        setTimeout(() => {
          if (cancelled) return;
          navigate('/login', {
            replace: true,
            state: { flash: { type: 'success', text: 'Cuenta verificada. Inicia sesión para continuar.' } },
          });
        }, REDIRECT_DELAY_MS);
      } catch (err) {
        if (cancelled) return;
        console.error('[AuthAction verifyEmail]', err);
        setError(actionErrorMessage(err));
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [mode, oobCode, navigate]);

  // ─── resetPassword: verificar oobCode → mostrar form ───
  useEffect(() => {
    if (mode !== 'resetPassword' || !oobCode) return;
    let cancelled = false;
    (async () => {
      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        if (cancelled) return;
        setResetEmail(email);
        setStatus('form');
      } catch (err) {
        if (cancelled) return;
        console.error('[AuthAction verifyPasswordResetCode]', err);
        setError(actionErrorMessage(err));
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [mode, oobCode]);

  const pwdChecks = [
    { ok: pwd.length >= 8, t: 'Al menos 8 caracteres' },
    { ok: /[A-Z]/.test(pwd) && /[a-z]/.test(pwd), t: 'Una mayúscula y una minúscula' },
    { ok: /[\d!@#$%^&*]/.test(pwd), t: 'Un número o símbolo' },
  ];
  const pwdStrong = pwdChecks.every((c) => c.ok);
  const pwdMatches = pwd && pwd === confirm;
  const canSubmit = pwdStrong && pwdMatches && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, pwd);
      setStatus('success');
      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { flash: { type: 'success', text: 'Contraseña actualizada. Inicia sesión con tu nueva contraseña.' } },
        });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      console.error('[AuthAction confirmPasswordReset]', err);
      setError(actionErrorMessage(err));
      setStatus('error');
      setSubmitting(false);
    }
  };

  // ── Header común
  const headerBlock = (
    <>
      <Helmet>
        <title>{mode === 'resetPassword' ? 'Nueva contraseña' : 'Verifica tu cuenta'} · Edge Elite</title>
      </Helmet>
      <AuthLogo />
    </>
  );

  // ── Estado: verificando (loading)
  if (status === 'verifying') {
    return (
      <div className="mobile-root">
        <section
          className="screen"
          style={{
            paddingTop: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            paddingBottom: 40,
          }}
        >
          {headerBlock}
          <div
            style={{
              width: 88, height: 88, borderRadius: 999,
              background: 'var(--accent-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: 12, marginBottom: 24,
            }}
          >
            <div
              style={{
                width: 36, height: 36, borderRadius: 999,
                border: '3px solid var(--accent)',
                borderTopColor: 'transparent',
                animation: 'authaction-spin 0.9s linear infinite',
              }}
            />
            <style>{`@keyframes authaction-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em',
              margin: 0, marginBottom: 8,
            }}
          >
            {mode === 'resetPassword' ? 'Validando enlace…' : 'Verificando tu cuenta…'}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, maxWidth: 320, lineHeight: 1.5 }}>
            Esto toma solo un segundo.
          </p>
        </section>
      </div>
    );
  }

  // ── Estado: éxito (verifyEmail completado o reset password completado)
  if (status === 'success') {
    return (
      <div className="mobile-root">
        <section
          className="screen"
          style={{
            paddingTop: 60,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', paddingBottom: 40,
          }}
        >
          {headerBlock}
          <div
            style={{
              width: 88, height: 88, borderRadius: 999,
              background: 'rgba(34,197,94,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: 12, marginBottom: 24,
            }}
          >
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5 9-11" />
            </svg>
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em',
              margin: 0, marginBottom: 8,
            }}
          >
            {mode === 'resetPassword' ? 'Contraseña actualizada' : 'Cuenta verificada'}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, marginBottom: 22, maxWidth: 320, lineHeight: 1.5 }}>
            Te llevamos al inicio de sesión…
          </p>
          <button
            className="btn-primary accent"
            style={{ width: '100%', maxWidth: 320 }}
            onClick={() => navigate('/login', { replace: true })}
          >
            Ir a iniciar sesión
          </button>
        </section>
      </div>
    );
  }

  // ── Estado: error o modo no soportado
  if (status === 'error' || status === 'unsupported') {
    return (
      <div className="mobile-root">
        <section
          className="screen"
          style={{
            paddingTop: 60,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', paddingBottom: 40,
          }}
        >
          {headerBlock}
          <div
            style={{
              width: 88, height: 88, borderRadius: 999,
              background: 'rgba(220,38,38,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginTop: 12, marginBottom: 24,
            }}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="13" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em',
              margin: 0, marginBottom: 8,
            }}
          >
            No pudimos completar la operación
          </h2>
          <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, marginBottom: 22, maxWidth: 320, lineHeight: 1.5 }}>
            {error || 'Intenta abrir el enlace de nuevo desde tu correo.'}
          </p>
          {mode === 'resetPassword' ? (
            <button
              className="btn-primary accent"
              style={{ width: '100%', maxWidth: 320 }}
              onClick={() => navigate('/recuperar', { replace: true })}
            >
              Volver a solicitar
            </button>
          ) : (
            <button
              className="btn-primary accent"
              style={{ width: '100%', maxWidth: 320 }}
              onClick={() => navigate('/login', { replace: true })}
            >
              Ir a iniciar sesión
            </button>
          )}
        </section>
      </div>
    );
  }

  // ── Estado: form (resetPassword)
  return (
    <div className="mobile-root">
      <section className="screen" style={{ paddingTop: 28, paddingBottom: 40 }}>
        {headerBlock}
        <div className="screen-head">
          <h2>Crea tu nueva contraseña</h2>
          <p>
            Estás cambiando la contraseña de{' '}
            <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{resetEmail || 'tu cuenta'}</strong>.
          </p>
        </div>
        <ErrorBanner msg={error} />
        <div className="input-group">
          <Field
            label="Nueva contraseña"
            type={showPwd ? 'text' : 'password'}
            placeholder="Mínimo 8 caracteres"
            value={pwd}
            onChange={setPwd}
            autoComplete="new-password"
            right={
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                style={{
                  background: 'none', border: 0, padding: 0, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--muted)',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            }
          />
          <Field
            label="Confirma la contraseña"
            type={showPwd ? 'text' : 'password'}
            placeholder="Vuelve a escribirla"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            error={confirm && !pwdMatches ? 'No coincide con la nueva contraseña' : ''}
          />
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {pwdChecks.map((c, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.ok ? '#22C55E' : 'var(--muted)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                {c.ok ? <path d="M5 12l5 5 9-11" /> : <circle cx="12" cy="12" r="9" />}
              </svg>
              {c.t}
            </li>
          ))}
        </ul>

        <button
          className="btn-primary accent"
          style={{
            width: '100%',
            opacity: canSubmit ? 1 : 0.5,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Guardando…' : 'Guardar nueva contraseña'}
        </button>
      </section>
    </div>
  );
}
